import React, { useState } from 'react';
import { Info, Eye, EyeOff, Copy, Check } from 'lucide-react';

interface LoginHelpProps {
  className?: string;
}

const LoginHelp: React.FC<LoginHelpProps> = ({ className = '' }) => {
  const [showPasswords, setShowPasswords] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const accounts = [
    {
      type: 'Admin Account',
      email: 'admin@example.com',
      password: 'Password123!',
      role: 'Administrator',
      description: 'Vollzugriff auf alle Funktionen'
    },
    {
      type: 'User Account',
      email: 'user@example.com', 
      password: 'Password123!',
      role: 'User',
      description: 'Standard-Benutzerzugriff'
    }
  ];

  return (
    <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-start space-x-3">
        <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-medium text-blue-900 mb-2">
            Demo Login-Daten
          </h3>
          
          <div className="space-y-3">
            {accounts.map((account, index) => (
              <div key={index} className="bg-white rounded-md p-3 border border-blue-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">
                    {account.type}
                  </span>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {account.role}
                  </span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-600 w-16">Email:</span>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded flex-1">
                      {account.email}
                    </code>
                    <button
                      onClick={() => copyToClipboard(account.email, `email-${index}`)}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Email kopieren"
                    >
                      {copiedField === `email-${index}` ? (
                        <Check className="h-3 w-3 text-green-600" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </button>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-600 w-16">Password:</span>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded flex-1">
                      {showPasswords ? account.password : '••••••••••••'}
                    </code>
                    <button
                      onClick={() => copyToClipboard(account.password, `password-${index}`)}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Password kopieren"
                    >
                      {copiedField === `password-${index}` ? (
                        <Check className="h-3 w-3 text-green-600" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </button>
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 mt-2">
                  {account.description}
                </p>
              </div>
            ))}
          </div>
          
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-blue-100">
            <button
              onClick={() => setShowPasswords(!showPasswords)}
              className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
            >
              {showPasswords ? (
                <>
                  <EyeOff className="h-3 w-3" />
                  <span>Passwörter verbergen</span>
                </>
              ) : (
                <>
                  <Eye className="h-3 w-3" />
                  <span>Passwörter anzeigen</span>
                </>
              )}
            </button>
            
            <div className="text-xs text-gray-500">
              Klicken zum Kopieren
            </div>
          </div>
          
          <div className="mt-3 pt-3 border-t border-blue-100">
            <p className="text-xs text-gray-600">
              <strong>OAuth Login:</strong> Google und Microsoft sind ebenfalls verfügbar.
              Neue Accounts werden automatisch erstellt.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginHelp;
